import { useState } from "react";
import { useToast } from "@/shared/hooks/use-toast";
import { useTranslations } from "use-intl";
import { useNavigate } from "react-router-dom";
import useAuth from "./use-auth";

export const useGoogleOAuth = () => {
  const { toast } = useToast();
  const t = useTranslations();
  const navigate = useNavigate();
  const { refreshAuthStatus, getAuthRedirectPath } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleGoogleOAuth = () => {
    setIsSubmitting(true);
    const baseUrl = import.meta.env.VITE_API_URL || "https://api.lojal.app";
    const authUrl = `${baseUrl}/auth/google?platform=web`;

    const popup = window.open(
      authUrl,
      "google-oauth",
      "width=500,height=600,scrollbars=yes,resizable=yes,top=100,left=100"
    );

    if (!popup) {
      toast({
        title: t(`oauth.error.title`),
        description: t(`oauth.error.description`),
        variant: "destructive",
      });
      return;
    }

    const cleanup = () => {
      if (popup && !popup.closed) popup.close();
      setIsSubmitting(false);
      // Clear the OAuth status from localStorage
      localStorage.removeItem("oauth-popup-status");
    };

    // Set initial OAuth status
    localStorage.setItem("oauth-popup-status", "open");

    // Poll localStorage for OAuth completion instead of popup.closed
    const pollWindow = setInterval(async () => {
      const oauthStatus = localStorage.getItem("oauth-popup-status");

      if (oauthStatus === "completed" || oauthStatus === "cancelled") {
        clearInterval(pollWindow);

        if (oauthStatus === "completed") {
          // Refresh auth status in AuthProvider and wait for it to complete
          try {
            const authenticatedUser = await refreshAuthStatus();
            if (authenticatedUser) {
              // Use centralized helper to determine redirect path based on fresh user object
              const redirectPath = getAuthRedirectPath(authenticatedUser);
              navigate(redirectPath, { replace: true });
            } else {
              // Auth failed, redirect to login
              navigate("/login", { replace: true });
            }
          } catch {
            // Error during auth, redirect to login
            navigate("/login", { replace: true });
          }
          setIsSubmitting(false);
        } else {
          // OAuth was cancelled, just reset the loading state
          setIsSubmitting(false);
        }

        // Clean up localStorage
        localStorage.removeItem("oauth-popup-status");
      }
    }, 1000);

    // Cleanup after 5 minutes
    setTimeout(() => {
      clearInterval(pollWindow);
      setIsSubmitting(false);
      cleanup();
    }, 300000);
  };

  return {
    handleGoogleOAuth,
    isSubmitting,
  };
};
